import React from "react";
import { HelmetProvider } from "react-helmet-async";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useOutletContext,
  useLocation,
} from "react-router-dom";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";

import i18n from "i18";
import menuPages from "menuPages";
import "./styles/index.css";
import _helper from "actions/_helper";
import PrivacyPolicy from "./components/PrivacyPolicy";
import Post from "./pages/post";
import { ThemeProvider, ThemeContext } from "./ThemeContext";
import CookiePolicy from "./components/CookiePolicy";
import MobileMessage from "./components/MobileInfo/MobileMessage";
import TermsConditions from "./components/TermsConditions";
import { GoogleOAuthProvider } from "@react-oauth/google";
import AppContext from "./AppContext";
import _request from "./actions/_request";
import config from "./config";
import { cookieConsentTranslations } from "./cookieConsentTranslations";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const { pages } = menuPages;

// Redirect /api/payment/stripe/:type → /app/stripe/:type preserving query params
const StripeRedirect = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

const buildMuiTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: { main: "#42c0c7" },
      ...(mode === "dark"
        ? {
            background: { default: "#0b0911", paper: "#1d1929" },
            text: { primary: "#ffffff", secondary: "#a5a3a9" },
            divider: "rgba(255,255,255,0.2)",
          }
        : {
            background: { default: "#ffffff", paper: "#ffffff" },
            text: { primary: "#212832", secondary: "#666666" },
            divider: "rgba(0,0,0,0.2)",
          }),
    },
    typography: {
      fontFamily: 'Montserrat, "Helvetica Neue", Helvetica, Arial, sans-serif',
    },
  });

// Bridges the app's ThemeContext to MUI so palette mode flips with the app theme.
function MuiThemeBridge({ children }) {
  const { theme } = React.useContext(ThemeContext);
  const muiTheme = React.useMemo(
    () => buildMuiTheme(theme === "dark" ? "dark" : "light"),
    [theme]
  );
  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
}

export default class App extends React.Component {
  state = {
    metaData: null,
    wasLoaded: false,
  };

  loadMetaData = (callback, force = false) => {
    if ((this.state.wasLoaded || this.state.loading) && !force) {
      if (callback) callback(this.state.metaData);
      return;
    }

    this.setState({ loading: true }, async () => {
      const [res, creditsRes] = await Promise.all([
        _request.users.getMeta(),
        _request.aiCredits.getBalance(),
      ]);
      const metaData = res?.data || {};

      metaData.userPackage =
        (metaData.packages || []).find(
          (it) => it.id === metaData.user?.package
        ) || {};
      metaData.userPackage.permissions =
        metaData.userPackage.permissions || "{}";
      metaData.userInfo = metaData.user;

      if (creditsRes?.isOk && creditsRes?.data) {
        metaData.ai_credits = creditsRes.data;
      }

      this.setState({ loading: false, wasLoaded: true, metaData }, () => {
        if (callback) callback(metaData);
      });
    });
  };

  refreshCredits = async () => {
    const res = await _request.aiCredits.getBalance();
    if (res?.isOk && res?.data) {
      this.setState(({ metaData }) => ({
        metaData: { ...metaData, ai_credits: res.data },
      }));
    }
  };

  componentDidMount() {
    _helper.setFileSystem(document.body);

    // Устанавливаем глобальную ссылку на экземпляр App для _helper
    globalThis.app = this;

    this.initializeCookieConsent();
  }

  componentWillUnmount() {
    // Очищаем глобальную ссылку
    globalThis.app = null;
  }

  async changeLang(lang) {
    // console.log("Changing language to:", lang);
    localStorage.language = lang;
    await i18n.changeLanguage(lang);
    // console.log("Language changed, current language:", i18n.language);
    this.initializeCookieConsent(); // Обновляем Cookie Consent с новым языком
    this.forceUpdate();
    // console.log("App force updated");
  }

  // Cache wrapped route components by their source component so each page keeps a
  // STABLE element type across App re-renders. Previously a new wrapper function
  // was created on every render, so react-router treated each page as a brand-new
  // component type and fully remounted it on ANY App state change (e.g.
  // refreshCredits). That wiped page-local state and tore down modals mid-flow —
  // notably the AI Calendar wizard closed itself after each credit-spending step.
  pagePropsCache = new Map();

  pageProps(Component) {
    if (this.pagePropsCache.has(Component)) {
      return this.pagePropsCache.get(Component);
    }

    const Wrapped = (props) => {
      const navigate = useNavigate();
      const context = useOutletContext();
      return <Component {...props} {...(context || {})} navigate={navigate} />;
    };

    this.pagePropsCache.set(Component, Wrapped);
    return Wrapped;
  }

  insertRoute(props, childs = []) {
    if (childs?.length) return <Route {...props}>{childs}</Route>;

    return <Route {...props} />;
  }

  insertIndexRoute(path, index) {
    const to = `${path}/${index}`;

    return this.insertRoute({
      index: true,
      key: path,
      path,
      element: <Navigate to={to} replace />,
    });
  }

  insertMapRoute(key, obj) {
    const path = key.length < 2 ? key : `/${key}`;
    const compClass = obj?.prototype ? obj : obj?.view;
    const props = { key, path };
    const childs = [];

    if (compClass) props.Component = this.pageProps(compClass);

    if (obj?.toUpperCase && obj.length) {
      props.element = <Navigate to={`/${obj}`} replace />;
    } else {
      const { index, items } = obj || {};
      let keys = Object.keys(items || {});

      if (keys?.length) {
        if (index) childs.push(this.insertIndexRoute(path, index));

        keys.forEach((itKey) => {
          childs.push(this.insertMapRoute(`${key}/${itKey}`, items[itKey]));
        });
      }
    }

    return this.insertRoute(props, childs);
  }
  initializeCookieConsent() {
    const currentLang = i18n.language || "en";
    const translations =
      cookieConsentTranslations[currentLang] || cookieConsentTranslations.en;

    CookieConsent.run({
      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {
          enabled: false,
          readonly: false,
        },
      },

      language: {
        default: currentLang,
        translations: {
          [currentLang]: translations,
        },
      },
      guiOptions: {
        consentModal: {
          layout: "box",
          position: "bottom left",
          flipButtons: false,
          equalWeightButtons: true,
        },
        preferencesModal: {
          layout: "box",
          position: "left right",
          flipButtons: false,
          equalWeightButtons: true,
        },
      },
    });
  }

  render() {
    const contextValue = {
      ...this.state,
      loadMetaData: this.loadMetaData,
      refreshCredits: this.refreshCredits,
    };

    return (
      <AppContext.Provider value={contextValue}>
        <HelmetProvider>
          <ThemeProvider>
            <GoogleOAuthProvider clientId={config.googleClientId}>
            <MuiThemeBridge>
              <CssBaseline />
              <Router>
                <Routes>
                  {Object.keys(pages).map((key) =>
                    this.insertMapRoute(key, pages[key])
                  )}
                  <Route path="/post/:id" element={<Post />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route
                    path="/terms-conditions"
                    element={<TermsConditions />}
                  />
                  {/* Stripe payment return URLs from the backend */}
                  <Route
                    path="/api/payment/stripe/success"
                    element={<StripeRedirect to="/app/stripe/success" />}
                  />
                  <Route
                    path="/api/payment/stripe/failed"
                    element={<StripeRedirect to="/app/stripe/failed" />}
                  />
                </Routes>
              </Router>
              <MobileMessage />
            </MuiThemeBridge>
            </GoogleOAuthProvider>
          </ThemeProvider>
        </HelmetProvider>
      </AppContext.Provider>
    );
  }
}
